import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Autocomplete, Drawer, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { updatePageGrant } from "./accessApi";
import { PAGE_NOTES } from "./accessCatalog";
import { Notice, RoleChip, UserAvatar, peopleWhoCanOpen } from "./accessUi";
import { requestSessionRefresh } from "../../../utils/liveSession";

const PEOPLE_SHOWN = 60;

// Edit one page's extra access. Every add/remove saves straight away (the
// server applies them atomically) and reaches people within ~20 seconds.
const PageAccessDrawer = ({ page, grant, users, roleCatalog, actorId, onGrantChange, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [roleInput, setRoleInput] = useState("");
  const [personInput, setPersonInput] = useState("");
  const [showPeople, setShowPeople] = useState(false);

  const extraRoles = useMemo(() => grant?.roles || [], [grant]);
  const extraUserIds = useMemo(() => grant?.users || [], [grant]);
  const usersById = useMemo(() => new Map(users.filter((u) => u.id).map((u) => [u.id, u])), [users]);
  const extraPeople = extraUserIds.map(
    (id) => usersById.get(id) || { id, name: "Unknown user", missing: true }
  );

  const roleOptions = useMemo(
    () =>
      roleCatalog
        .map((r) => r.name)
        .filter((name) => !page.roles.includes(name) && !extraRoles.includes(name)),
    [roleCatalog, page.roles, extraRoles]
  );

  const allowedRoles = useMemo(() => new Set([...page.roles, ...extraRoles]), [page.roles, extraRoles]);
  const personOptions = useMemo(
    () =>
      users
        .filter((u) => u.id && !u.deactivated && !extraUserIds.includes(u.id))
        .map((u) => ({
          ...u,
          viaRole: (u.roles || []).some((r) => allowedRoles.has(r)),
        })),
    [users, extraUserIds, allowedRoles]
  );

  const canOpen = useMemo(() => peopleWhoCanOpen(page, grant, users), [page, grant, users]);

  const save = async (change, success) => {
    setBusy(true);
    setMessage(null);
    try {
      const { grant: next } = await updatePageGrant(actorId, {
        key: page.key,
        name: page.name,
        redirect: page.redirect,
        ...change,
      });
      onGrantChange(next);
      setMessage({ type: "success", text: success });
      requestSessionRefresh();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Couldn't save that change." });
    } finally {
      setBusy(false);
    }
  };

  const note = PAGE_NOTES[page.key];

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
      <div className="flex h-full flex-col">
        <div className="app-divider flex items-start gap-3 border-b px-5 py-4">
          <span className="app-icon-tile mt-0.5 h-10 w-10 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
            <LockOpenRoundedIcon fontSize="small" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="app-eyebrow">{page.section}</p>
            <h2 className="truncate text-lg font-semibold tracking-tight">{page.name}</h2>
            <p className="app-muted truncate font-mono text-xs">
              {page.redirect || page.paths[0]}
              {page.hasDetail && page.redirect ? " + detail pages" : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="app-icon-btn -mr-2 shrink-0">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          <Notice message={message} />

          {note && (
            <p className="flex gap-2 rounded-xl bg-amber-500/15 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              <InfoOutlinedIcon sx={{ fontSize: 18 }} className="mt-0.5 shrink-0" />
              {note}
            </p>
          )}

          <section>
            <p className="app-eyebrow mb-2">Always allowed</p>
            <div className="flex flex-wrap gap-1.5">
              {page.roles.map((role) => (
                <RoleChip key={role} role={role} tone="base" />
              ))}
            </div>
            <p className="app-muted mt-2 text-xs">Set in code. Extra access below only adds people — it never takes these away.</p>
          </section>

          <section className="app-divider border-t pt-5">
            <p className="app-eyebrow mb-2">Extra roles</p>
            <div className="flex flex-wrap gap-1.5">
              {extraRoles.length ? (
                extraRoles.map((role) => (
                  <RoleChip
                    key={role}
                    role={role}
                    disabled={busy}
                    onRemove={() => save({ removeRoles: [role] }, `${role} can no longer open this page.`)}
                  />
                ))
              ) : (
                <p className="app-muted text-sm">None yet.</p>
              )}
            </div>
            <Autocomplete
              className="mt-3"
              size="small"
              options={roleOptions}
              value={null}
              inputValue={roleInput}
              onInputChange={(e, value) => setRoleInput(value)}
              onChange={(e, role) => {
                if (!role) return;
                setRoleInput("");
                save({ addRoles: [role] }, `Everyone with ${role} can now open this page.`);
              }}
              disabled={busy}
              blurOnSelect
              renderInput={(params) => <TextField {...params} placeholder="Add a role…" />}
              noOptionsText="No other roles"
            />
          </section>

          <section className="app-divider border-t pt-5">
            <p className="app-eyebrow mb-2">Extra people</p>
            {extraPeople.length ? (
              <ul className="space-y-1">
                {extraPeople.map((person) => (
                  <li key={person.id} className="flex items-center gap-3 rounded-xl px-1 py-1.5">
                    <UserAvatar user={person} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {person.name}
                        {person.deactivated && <span className="app-muted"> · deactivated</span>}
                      </p>
                      <p className="app-muted truncate text-xs">
                        {[person.title, person.branch].filter(Boolean).join(" · ") || person.email}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        save({ removeUsers: [person.id] }, `${person.name} can no longer open this page through a direct grant.`)
                      }
                      disabled={busy}
                      className="app-btn app-btn-sm app-btn-ghost text-rose-700 dark:text-rose-300"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="app-muted text-sm">None yet.</p>
            )}
            <Autocomplete
              className="mt-3"
              size="small"
              options={personOptions}
              value={null}
              inputValue={personInput}
              onInputChange={(e, value) => setPersonInput(value)}
              onChange={(e, person) => {
                if (!person) return;
                setPersonInput("");
                save({ addUsers: [person.id] }, `${person.name} can now open this page. They'll get a notification.`);
              }}
              getOptionLabel={(u) => u.name || u.email || ""}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                const hits = q
                  ? options.filter((u) =>
                      [u.name, u.email, u.title, u.branch].filter(Boolean).some((f) => f.toLowerCase().includes(q))
                    )
                  : options;
                return hits.slice(0, 50);
              }}
              renderOption={(props, u) => (
                <li {...props} key={u.id}>
                  <div className="min-w-0">
                    <p className="truncate text-sm">{u.name}</p>
                    <p className="app-muted truncate text-xs">
                      {[u.title, u.branch].filter(Boolean).join(" · ")}
                      {u.viaRole ? " · already has access through a role" : ""}
                    </p>
                  </div>
                </li>
              )}
              disabled={busy}
              blurOnSelect
              renderInput={(params) => <TextField {...params} placeholder="Add a person…" />}
              noOptionsText="No matching people"
            />
            <p className="app-muted mt-2 text-xs">People who have never signed in can't be added until they do.</p>
          </section>

          <section className="app-divider border-t pt-5">
            <div className="flex items-center justify-between gap-2">
              <p className="app-eyebrow">Who can open it</p>
              {page.redirect && (
                <Link to={page.redirect} className="app-link inline-flex items-center gap-1 text-xs">
                  Open page <ArrowOutwardRoundedIcon sx={{ fontSize: 14 }} />
                </Link>
              )}
            </div>
            <p className="mt-1 text-sm">
              <strong className="tabular-nums">{canOpen.length}</strong> active {canOpen.length === 1 ? "person" : "people"}
              {canOpen.length > 0 && (
                <button type="button" onClick={() => setShowPeople((v) => !v)} className="app-link ml-2 text-xs">
                  {showPeople ? "Hide" : "Show"}
                </button>
              )}
            </p>
            {showPeople && (
              <p className="app-muted mt-2 text-xs leading-relaxed">
                {canOpen
                  .slice(0, PEOPLE_SHOWN)
                  .map((u) => u.name)
                  .join(", ")}
                {canOpen.length > PEOPLE_SHOWN ? `, and ${canOpen.length - PEOPLE_SHOWN} more` : ""}
              </p>
            )}
          </section>
        </div>
      </div>
    </Drawer>
  );
};

export default PageAccessDrawer;
